import { useCallback, useEffect, useRef, useState, type TouchEvent } from 'react';
import {
  formatKoreanDate,
  formatShortDate,
  nameFontStack,
  signatureFontStack,
  type InvitationData,
  type ThemeKey,
} from '@/types/wedding';
import TransitBlock from './inv/TransitBlock';
import RsvpBlock from './inv/RsvpBlock';
import GuestbookBlock from './inv/GuestbookBlock';
import CountdownBlock from './inv/CountdownBlock';
import ShareBar from './inv/ShareBar';

interface Props {
  data: InvitationData;
  theme: ThemeKey;
  interactive?: boolean;
  invitationId?: string;
  shareUrl?: string;
}

function mapEmbedSrc(data: InvitationData): string | null {
  const query = data.mapQuery.trim() || data.venueName.trim() || data.address.trim();
  if (!query) return null;
  return `https://www.google.com/maps?q=${encodeURIComponent(query)}&hl=ko&z=15&output=embed`;
}

function mapLinkHref(data: InvitationData): string {
  const query = data.mapQuery.trim() || data.venueName.trim() || data.address.trim();
  return `https://www.google.com/maps?q=${encodeURIComponent(query)}`;
}

export default function InvitationView({
  data,
  theme,
  interactive = true,
  invitationId = '',
  shareUrl = '',
}: Props) {
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [copied, setCopied] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'slide'>('grid');
  const [slideIndex, setSlideIndex] = useState(0);
  const [musicMuted, setMusicMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const touchStartX = useRef<number | null>(null);

  const photos = data.photos;
  const cover = photos[0];
  const galleryPhotos = photos;

  const closeLightbox = useCallback(() => setLightbox(null), []);

  useEffect(() => {
    if (photos.length > 0 && slideIndex > photos.length - 1) setSlideIndex(0);
  }, [photos.length, slideIndex]);

  useEffect(() => {
    if (lightbox === null || !interactive) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') setLightbox((i) => (i === null ? i : (i + 1) % photos.length));
      if (e.key === 'ArrowLeft')
        setLightbox((i) => (i === null ? i : (i - 1 + photos.length) % photos.length));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, photos.length, closeLightbox, interactive]);

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      window.setTimeout(() => setCopied(''), 1800);
    } catch {
      setCopied('');
    }
  };

  // 배경음악: 하객이 처음 화면을 터치/스크롤하는 순간 자동 재생
  useEffect(() => {
    if (!interactive || !data.musicUrl) return undefined;
    const el = audioRef.current;
    if (!el) return undefined;
    el.muted = false;
    let started = false;
    const remove = () => {
      document.removeEventListener('pointerdown', attempt);
      document.removeEventListener('touchstart', attempt);
      document.removeEventListener('click', attempt);
      document.removeEventListener('keydown', attempt);
      document.removeEventListener('scroll', attempt, true);
    };
    const attempt = () => {
      if (started) return;
      el.play()
        .then(() => {
          started = true;
          setMusicMuted(false);
          remove();
        })
        .catch(() => {
          /* 다음 제스처에서 재시도 */
        });
    };
    document.addEventListener('pointerdown', attempt, { passive: true });
    document.addEventListener('touchstart', attempt, { passive: true });
    document.addEventListener('click', attempt, { passive: true });
    document.addEventListener('keydown', attempt);
    document.addEventListener('scroll', attempt, true);
    attempt();
    return remove;
  }, [interactive, data.musicUrl]);

  // 상단 스피커 버튼 — 누를 때마다 음소거 / 재생 전환
  const toggleMusic = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.muted || el.paused) {
      el.muted = false;
      if (interactive) void el.play().catch(() => {});
      setMusicMuted(false);
    } else {
      el.muted = true;
      setMusicMuted(true);
    }
  };

  const goSlide = (dir: -1 | 1) => {
    if (photos.length === 0) return;
    setSlideIndex((i) => (i + dir + photos.length) % photos.length);
  };

  const onSlideTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };

  const onSlideTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
    if (Math.abs(dx) > 40) goSlide(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  const dateText = formatKoreanDate(data.date) || '날짜를 입력해 주세요';
  const shortDate = formatShortDate(data.date);
  const inviteTitle =
    data.groom.name || data.bride.name
      ? `${data.groom.name || '신랑'} · ${data.bride.name || '신부'} 결혼식에 초대합니다`
      : '우리 결혼식에 초대합니다';

  const whenRows = [
    { k: '날짜', v: formatKoreanDate(data.date) },
    { k: '시간', v: data.time },
    { k: '예식장', v: [data.venueName, data.hall].filter(Boolean).join(' ') },
    { k: '주소', v: data.address },
  ].filter((r) => r.v);

  return (
    <div className={`inv theme-${theme}`}>
      {/* BACKGROUND MUSIC — 자동 재생 + 상단 스피커 버튼으로 음소거 */}
      {data.musicUrl ? (
        <audio
          ref={audioRef}
          src={data.musicUrl}
          loop
          preload="auto"
          onVolumeChange={(e) => setMusicMuted(e.currentTarget.muted)}
        />
      ) : null}

      {data.musicUrl ? (
        <div className="inv-music-top">
          <button
            type="button"
            className={`inv-music-btn${musicMuted ? ' is-muted' : ''}`}
            onClick={toggleMusic}
            aria-label={musicMuted ? '배경음악 켜기' : '배경음악 음소거'}
            aria-pressed={!musicMuted}
            title={musicMuted ? '배경음악 켜기' : '배경음악 음소거'}
          >
            <i
              className={musicMuted ? 'ri-volume-mute-line' : 'ri-volume-up-line'}
              aria-hidden="true"
            />
          </button>
        </div>
      ) : null}

      {/* COUNTDOWN */}
      <CountdownBlock
        date={data.date}
        time={data.time}
        groomName={data.groom.name}
        brideName={data.bride.name}
      />

      {/* HERO */}
      <section className="inv-hero">
        {cover ? (
          <img className="hero-img" src={cover} alt="대표 사진" />
        ) : (
          <div className="hero-fallback" aria-hidden="true" />
        )}
        <div className="inv-hero-in">
          <p className="inv-eyebrow">We invite you</p>
          <h2
            className="inv-names"
            style={
              nameFontStack(data.nameFont) ? { fontFamily: nameFontStack(data.nameFont) } : undefined
            }
          >
            {data.groom.name || '신랑'}
            <em> &amp; </em>
            {data.bride.name || '신부'}
          </h2>
          <span className="inv-hero-rule" aria-hidden="true" />
          <p className="inv-hero-date">{shortDate || dateText}</p>
          {(data.venueName || data.time) && (
            <p className="inv-hero-venue">
              {[data.time, data.venueName].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      </section>

      {/* GREETING — 신랑 · 신부가 전하는 말 (입력한 내용만 표시) */}
      {data.quote || data.greeting || data.greetingSign ? (
        <section className="inv-sec center">
          <div className="inv-leaves" aria-hidden="true">
            <i className="ri-leaf-line left" />
            <i className="ri-leaf-line right" />
          </div>
          {data.quote ? <p className="inv-quote">{data.quote}</p> : null}
          {data.greeting ? <p className="inv-greeting-text">{data.greeting}</p> : null}
          {data.greetingSign ? (
            <p
              className={`inv-sign${data.signatureFont && data.signatureFont !== 'default' ? ' is-script' : ''}`}
              style={
                signatureFontStack(data.signatureFont)
                  ? { fontFamily: signatureFontStack(data.signatureFont) }
                  : undefined
              }
            >
              {data.greetingSign}
            </p>
          ) : null}
        </section>
      ) : null}

      {/* COUPLE */}
      <section className="inv-sec">
        <p className="inv-kicker">The Groom &amp; The Bride</p>
        <div className="inv-couple">
          <div className="inv-person">
            <span className="role">Groom</span>
            <h3
              className="pname"
              style={
                nameFontStack(data.nameFont)
                  ? { fontFamily: nameFontStack(data.nameFont) }
                  : undefined
              }
            >
              {data.groom.name || '신랑'}
            </h3>
            {data.groom.parents ? <p className="parents">{data.groom.parents}</p> : null}
            {data.groom.phone ? (
              <div className="inv-contact">
                <a className="inv-mini-btn" href={`tel:${data.groom.phone.replace(/[^0-9+]/g, '')}`}>
                  <i className="ri-phone-line" aria-hidden="true" />
                  전화
                </a>
                <a className="inv-mini-btn" href={`sms:${data.groom.phone.replace(/[^0-9+]/g, '')}`}>
                  <i className="ri-message-3-line" aria-hidden="true" />
                  문자
                </a>
              </div>
            ) : null}
          </div>
          <div className="inv-person">
            <span className="role">Bride</span>
            <h3
              className="pname"
              style={
                nameFontStack(data.nameFont)
                  ? { fontFamily: nameFontStack(data.nameFont) }
                  : undefined
              }
            >
              {data.bride.name || '신부'}
            </h3>
            {data.bride.parents ? <p className="parents">{data.bride.parents}</p> : null}
            {data.bride.phone ? (
              <div className="inv-contact">
                <a className="inv-mini-btn" href={`tel:${data.bride.phone.replace(/[^0-9+]/g, '')}`}>
                  <i className="ri-phone-line" aria-hidden="true" />
                  전화
                </a>
                <a className="inv-mini-btn" href={`sms:${data.bride.phone.replace(/[^0-9+]/g, '')}`}>
                  <i className="ri-message-3-line" aria-hidden="true" />
                  문자
                </a>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* WHEN & WHERE */}
      {whenRows.length > 0 ? (
        <section className="inv-sec">
          <p className="inv-kicker">When &amp; Where</p>
          <div className="inv-when">
            {whenRows.map((row) => (
              <div className="inv-when-row" key={row.k}>
                <span className="k">{row.k}</span>
                <span className="v">{row.v}</span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* GALLERY */}
      {galleryPhotos.length > 0 ? (
        <section className="inv-sec">
          <div className="inv-gallery-head">
            <p className="inv-kicker">Gallery</p>
            <div className="inv-view-toggle" role="tablist" aria-label="갤러리 보기 방식">
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === 'grid'}
                className={viewMode === 'grid' ? 'is-active' : ''}
                onClick={() => setViewMode('grid')}
              >
                <i className="ri-grid-fill" aria-hidden="true" />
                그리드
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === 'slide'}
                className={viewMode === 'slide' ? 'is-active' : ''}
                onClick={() => setViewMode('slide')}
              >
                <i className="ri-slideshow-line" aria-hidden="true" />
                슬라이드
              </button>
            </div>
          </div>
          <p className="inv-sec-sub">
            {viewMode === 'grid'
              ? '한 화면에 3장씩 보여요. 옆으로 넘겨 더 보고, 누르면 크게 볼 수 있어요.'
              : '좌우로 넘겨 사진을 감상할 수 있어요. 누르면 크게 볼 수 있어요.'}
          </p>

          {viewMode === 'grid' ? (
            <div className="inv-gallery">
              {galleryPhotos.map((src, i) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => interactive && setLightbox(i)}
                  aria-label={`사진 ${i + 1} 크게 보기`}
                >
                  <img src={src} alt={`웨딩 갤러리 ${i + 1}`} />
                </button>
              ))}
            </div>
          ) : (
            <div
              className="inv-slider"
              onTouchStart={onSlideTouchStart}
              onTouchEnd={onSlideTouchEnd}
            >
              <div
                className="inv-slider-track"
                style={{ transform: `translateX(-${slideIndex * 100}%)` }}
              >
                {galleryPhotos.map((src, i) => (
                  <button
                    key={src}
                    type="button"
                    className="inv-slide"
                    onClick={() => interactive && setLightbox(i)}
                    aria-label={`사진 ${i + 1} 크게 보기`}
                  >
                    <img src={src} alt={`웨딩 갤러리 ${i + 1}`} />
                  </button>
                ))}
              </div>
              {galleryPhotos.length > 1 ? (
                <>
                  <button
                    type="button"
                    className="inv-slide-nav prev"
                    onClick={() => goSlide(-1)}
                    aria-label="이전 사진"
                  >
                    <i className="ri-arrow-left-s-line" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="inv-slide-nav next"
                    onClick={() => goSlide(1)}
                    aria-label="다음 사진"
                  >
                    <i className="ri-arrow-right-s-line" aria-hidden="true" />
                  </button>
                  <div className="inv-slide-dots">
                    {galleryPhotos.map((src, i) => (
                      <button
                        key={src}
                        type="button"
                        className={i === slideIndex ? 'is-active' : ''}
                        onClick={() => setSlideIndex(i)}
                        aria-label={`${i + 1}번째 사진 보기`}
                      />
                    ))}
                  </div>
                </>
              ) : null}
              <span className="inv-slide-count">
                {slideIndex + 1} / {galleryPhotos.length}
              </span>
            </div>
          )}
        </section>
      ) : null}

      {/* LOCATION */}
      {mapEmbedSrc(data) ? (
        <section className="inv-sec">
          <p className="inv-kicker">Location</p>
          {data.address ? (
            <p style={{ margin: '0 0 16px', fontSize: 14, lineHeight: 1.7, color: 'var(--i-text)' }}>
              {data.address}
            </p>
          ) : null}
          <div className="inv-map">
            <iframe
              title="예식장 위치"
              src={mapEmbedSrc(data) as string}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="inv-contact" style={{ marginTop: 14 }}>
            <a
              className="inv-mini-btn"
              href={mapLinkHref(data)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="ri-map-pin-line" aria-hidden="true" />
              지도 길찾기
            </a>
          </div>
        </section>
      ) : null}

      {/* TRANSIT */}
      <TransitBlock transit={data.transit} />

      {/* ACCOUNTS */}
      {(data.groomAccount.number || data.brideAccount.number) && (
        <section className="inv-sec">
          <p className="inv-kicker">마음 전하실 곳</p>
          <div className="inv-accounts">
            {[
              { side: '신랑 측', account: data.groomAccount },
              { side: '신부 측', account: data.brideAccount },
            ]
              .filter((a) => a.account.number)
              .map((a) => (
                <div className="inv-account" key={a.side}>
                  <div className="info">
                    <span className="side">{a.side}</span>
                    <span className="num">
                      {[a.account.bank, a.account.number].filter(Boolean).join(' ')}
                    </span>
                    {a.account.holder ? (
                      <span className="holder">예금주 {a.account.holder}</span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="inv-copy"
                    onClick={() => void copy(a.side, a.account.number)}
                  >
                    <i
                      className={copied === a.side ? 'ri-check-line' : 'ri-file-copy-line'}
                      aria-hidden="true"
                    />
                    {copied === a.side ? '복사됨' : '복사'}
                  </button>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* RSVP */}
      {invitationId ? <RsvpBlock invitationId={invitationId} interactive={interactive} /> : null}

      {/* GUESTBOOK */}
      {invitationId ? (
        <GuestbookBlock invitationId={invitationId} interactive={interactive} />
      ) : null}

      <footer className="inv-foot">
        <p>
          {data.groom.name || '신랑'} · {data.bride.name || '신부'}
          <br />
          {dateText}
        </p>
      </footer>

      {/* SHARE */}
      {shareUrl ? <ShareBar shareUrl={shareUrl} title={inviteTitle} dateText={dateText} /> : null}

      {interactive && lightbox !== null && photos[lightbox] ? (
        <div className="inv-lightbox" onClick={closeLightbox} role="presentation">
          <img src={photos[lightbox]} alt="웨딩 사진 크게 보기" onClick={(e) => e.stopPropagation()} />
          <button type="button" className="inv-lb-close" onClick={closeLightbox} aria-label="닫기">
            <i className="ri-close-line" aria-hidden="true" />
          </button>
          {photos.length > 1 ? (
            <>
              <button
                type="button"
                className="inv-lb-nav prev"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightbox((i) => (i === null ? i : (i - 1 + photos.length) % photos.length));
                }}
                aria-label="이전 사진"
              >
                <i className="ri-arrow-left-s-line" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="inv-lb-nav next"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightbox((i) => (i === null ? i : (i + 1) % photos.length));
                }}
                aria-label="다음 사진"
              >
                <i className="ri-arrow-right-s-line" aria-hidden="true" />
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}